import React from "react";
import InvalidStopsPanel from "../InvalidStopsPanel.jsx";
import RouteSequencePanel from "../RouteSequencePanel.jsx";
import SuspendedDeliveriesList from "../SuspendedDeliveriesList.jsx";
import { normalizeCurrencyDisplay } from "../../utils/currencyDisplay.js";
import {
  ActionButton,
  EmptyState,
  HoverCard,
  joinClasses,
  MetricCard,
  PanelShell,
  Pill,
  StatusBadge,
  sectionTagClassName,
} from "../ResultsPrimitives.jsx";
import { dataAttributeProps } from "./shared.js";
import {
  buildNonComplianceAlertCaptureContext,
  serializeDispatchAlertCaptureContext,
} from "../../lib/dispatchAlertCaptureContext.js";

export function RouteHeaderSection({ interactions = {}, model }) {
  void interactions;
  const statusLabel = typeof model?.statusLabel === "string" ? model.statusLabel.trim() : "";
  const shouldRenderStatus = Boolean(statusLabel) && model?.statusHidden !== true;
  const routeHeaderStatusPillClassName = "rounded-sm! border-[rgba(121,108,90,0.18)]! bg-[linear-gradient(180deg,rgba(255,255,255,0.97),rgba(247,242,235,0.94))]! px-2 py-1 text-[0.62rem] font-medium! text-[rgb(95_107_103)]! shadow-[inset_0_1px_0_rgba(255,255,255,0.72)]";

  if (!shouldRenderStatus) {
    return null;
  }

  return (
    <div className="flex w-full min-w-0 items-center justify-end gap-2">
      <Pill tone="info" className={routeHeaderStatusPillClassName} data-route-target-status="true" data-route-tone={model.statusTone}>
        {statusLabel}
      </Pill>
    </div>
  );
}

function FormulaContent({ lines = [], note = null, total = null, totalLabel = "Total" }) {
  return (
    <div className="flex min-w-55 flex-col gap-1.5">
      {lines.map((line, index) => {
        if (!line?.label && !line?.value) {
          return <div key={index} className="h-1" />;
        }

        return (
          <div
            key={index}
            className="grid items-center gap-2 text-[12px]"
            style={{
              gridTemplateColumns: "100px 1fr",
            }}
          >
            <span className="truncate font-semibold text-content-ink">{line.label}</span>
            <span className="text-content-muted">
              {line.operator ? <span className="mr-1 text-content-faint">{line.operator}</span> : null}
              {normalizeCurrencyDisplay(line.value)}
            </span>
          </div>
        );
      })}

      {total ? (
        <div className="mt-0.5 flex items-center justify-between border-t-[0.5px] border-[rgba(0,0,0,0.08)] pt-2">
          <span className="text-[11px] text-content-muted">{totalLabel}</span>
          <span className="text-[14px] font-bold text-content-ink">= {normalizeCurrencyDisplay(total)}</span>
        </div>
      ) : null}

      {typeof note === "string" && note.trim() ? (
        <div className="border-t-[0.5px] border-[rgba(0,0,0,0.08)] pt-2">
          <DurationNoteContent note={note} />
        </div>
      ) : null}
    </div>
  );
}

function DurationNoteContent({ note }) {
  return <p className="max-w-70 text-[12px] leading-[1.6] text-[#374151]">{normalizeCurrencyDisplay(note)}</p>;
}

function CoutEstimeContent({ details }) {
  const hasFormulaData = Boolean(
    details?.distanceLabel
      || details?.durationLabel
      || details?.tarifKm
      || details?.tarifMin
      || details?.totalLabel,
  );

  return (
    <div className="flex flex-col gap-2.5">
      {hasFormulaData ? (
        <div className="flex min-w-65 flex-col gap-1.5">
        <div
          className="grid items-center gap-x-2.5 text-[12px]"
          style={{
            gridTemplateColumns: "70px 80px 16px 1fr",
          }}
        >
          <span className="font-semibold text-content-ink">Distance</span>
          <span className="text-right text-content-muted">{details.distanceLabel || "-"}</span>
          <span className="text-center text-content-faint">×</span>
          <span className="text-content-muted">{details.tarifKm ? `${details.tarifKm} $/km` : "-"}</span>
        </div>

        <div
          className="grid items-center gap-x-2.5 text-[12px]"
          style={{
            gridTemplateColumns: "70px 80px 16px 1fr",
          }}
        >
          <span className="font-semibold text-content-ink">Durée</span>
          <span className="text-right text-content-muted">{details.durationLabel || "-"}</span>
          <span className="text-center text-content-faint">×</span>
          <span className="text-content-muted">{details.tarifMin ? `${details.tarifMin} $/min` : "-"}</span>
        </div>

        {details.totalLabel ? (
          <div className="mt-1 flex items-center justify-between border-t-[0.5px] border-[rgba(0,0,0,0.08)] pt-2">
            <span className="text-[11px] text-content-muted">Total estimé</span>
            <span className="text-[14px] font-bold text-content-ink">= {normalizeCurrencyDisplay(details.totalLabel)}</span>
          </div>
        ) : null}
        </div>
      ) : null}

      {typeof details?.note === "string" && details.note.trim() ? (
        <div className="border-t-[0.5px] border-[rgba(0,0,0,0.08)] pt-2.5">
          <DurationNoteContent note={details.note} />
        </div>
      ) : null}
    </div>
  );
}

function resolveKpiHoverCardTitle(item) {
  if (item?.key === "estimated-cost") {
    return item.label;
  }

  if (item?.hoverCardDetails?.type === "formula" || item?.hoverCardDetails?.type === "note") {
    return item.title || item.label;
  }

  return item.label;
}

function resolveKpiHoverCardContent(item) {
  if (item?.key === "estimated-cost" && item?.hoverCardDetails) {
    return <CoutEstimeContent details={item.hoverCardDetails} />;
  }

  if (
    item?.hoverCardDetails?.type === "note"
    && typeof item.hoverCardDetails.note === "string"
  ) {
    return <DurationNoteContent note={item.hoverCardDetails.note} />;
  }

  if (
    item?.hoverCardDetails?.type === "formula"
    && Array.isArray(item.hoverCardDetails.lines)
  ) {
    return (
      <FormulaContent
        lines={item.hoverCardDetails.lines}
        note={item.hoverCardDetails.note || null}
        total={item.hoverCardDetails.total || null}
        totalLabel={item.hoverCardDetails.totalLabel || "Total"}
      />
    );
  }

  return item?.title || null;
}

function resolveObservabilityHoverAccentColor(tone = "neutral") {
  if (tone === "warning") {
    return "rgba(154,103,15,0.82)";
  }

  if (tone === "danger") {
    return "rgba(151,45,36,0.82)";
  }

  if (tone === "accent") {
    return "rgba(15,92,87,0.82)";
  }

  return "rgba(100,116,139,0.78)";
}

export function ObservabilityKpiSection({ className = "", dense = false, inline = false, items = [] }) {
  if (!items.length) {
    return null;
  }

  const gridClassName = inline
    ? "flex flex-nowrap items-end gap-0.75"
    : dense
    ? "grid gap-0.75 sm:grid-cols-2 xl:grid-cols-3"
    : "grid gap-1.25 sm:grid-cols-2 lg:grid-cols-3";
  const metricCardClassName = dense
    ? joinClasses([
      "gap-[2px]! rounded-[8px]! px-1.75! py-0.8!",
      inline ? "w-fit min-w-0" : "",
    ])
    : "gap-0.5! rounded-[8px]! px-2.5! py-1.35!";
  const metricLabelClassName = dense
    ? "overflow-hidden text-ellipsis whitespace-nowrap text-[0.49rem]! leading-none! tracking-[0.04em]!"
    : "overflow-hidden text-ellipsis whitespace-nowrap text-[0.55rem]! tracking-[0.05em]!";
  const metricValueClassName = dense
    ? "text-[0.76rem]! leading-none!"
    : "text-[0.84rem]! leading-[1.02]!";

  return (
    <div className={joinClasses([gridClassName, className])}>
        {items.map((item) => {
          const hoverCardContent = resolveKpiHoverCardContent(item);

          return (
            <HoverCard
              key={item.key}
              title={resolveKpiHoverCardTitle(item)}
              content={hoverCardContent}
              placement="top"
              delay={120}
              accentColor={resolveObservabilityHoverAccentColor(item?.tone || "neutral")}
              disabled={hoverCardContent == null}
            >
              <MetricCard
                className={metricCardClassName}
                label={item.label}
                labelClassName={metricLabelClassName}
                tone={item.tone || "neutral"}
                value={item.value}
                valueClassName={metricValueClassName}
              />
            </HoverCard>
          );
        })}
    </div>
  );
}

export function KpiGridSection({ model, showObservability = true, inline = false }) {
  const items = Array.isArray(model.items) ? model.items : [];
  const observabilityItems = Array.isArray(model.observabilityItems) ? model.observabilityItems : [];
  const inlineItems = showObservability ? [...items, ...observabilityItems] : items;
  const gridClassName = items.length >= 8
    ? "grid gap-1.5 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-[minmax(0,0.94fr)_minmax(0,0.94fr)_minmax(0,0.98fr)_minmax(84px,0.58fr)_minmax(0,0.98fr)_minmax(0,1.08fr)_minmax(0,1.36fr)_minmax(0,0.88fr)]"
    : "grid gap-1.5 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7";
  const metricCardClassName = "gap-0.75! rounded-[8px]! px-2.75! py-1.5!";
  const metricLabelClassName = "text-[0.59rem]! tracking-[0.055em]!";
  const metricValueClassName = "text-[0.88rem]! leading-[1.05]!";
  const inlineContainerClassName = "grid gap-1 sm:grid-cols-2 min-[841px]:flex min-[841px]:items-stretch min-[841px]:gap-0.75";
  const inlineMetricCardClassName = "gap-[2px]! rounded-[4px]! px-1.9! py-0.8! min-[841px]:min-w-0 min-[841px]:w-full";
  // Libellés et valeurs complets : chaque indicateur prend la largeur de son contenu.
  const inlineMetricLabelClassName = "whitespace-nowrap text-[0.5rem]! leading-none! tracking-[0.04em]!";
  const inlineMetricValueClassName = "whitespace-nowrap text-[0.76rem]! leading-none!";

  const renderMetricItem = (item, { compact = false } = {}) => {
    const hoverCardContent = resolveKpiHoverCardContent(item);
    const isEstimatedCost = item?.key === "estimated-cost";
    const metricTone = item?.tone || (item.isAccent && !isEstimatedCost ? "accent" : "neutral");
    const hoverAccentColor = item?.tone ? resolveObservabilityHoverAccentColor(metricTone) : "rgba(15,92,87,0.82)";
    const card = (
      <HoverCard
        title={resolveKpiHoverCardTitle(item)}
        content={hoverCardContent}
        placement="top"
        delay={120}
        accentColor={hoverAccentColor}
        disabled={hoverCardContent == null}
      >
        <MetricCard
          aria-label={item.ariaLabel || undefined}
          className={compact ? inlineMetricCardClassName : metricCardClassName}
          label={item.label}
          labelClassName={compact ? inlineMetricLabelClassName : metricLabelClassName}
          tone={isEstimatedCost ? "neutral" : metricTone}
          value={item.value}
          valueClassName={compact ? inlineMetricValueClassName : metricValueClassName}
          valueProps={dataAttributeProps(item.valueDataAttribute)}
          {...dataAttributeProps(item.cardDataAttribute)}
        />
      </HoverCard>
    );

    if (!compact) {
      return <React.Fragment key={item.key}>{card}</React.Fragment>;
    }

    return (
      <div key={item.key} className="min-w-0 min-[841px]:flex-[1_1_auto]">
        {card}
      </div>
    );
  };

  if (inline) {
    return (
      <div className={inlineContainerClassName}>
        {inlineItems.map((item) => renderMetricItem(item, { compact: true }))}
      </div>
    );
  }

  return (
    <div className="grid gap-1.5">
      <div className={gridClassName}>
        {items.map((item) => renderMetricItem(item))}
      </div>

      {showObservability ? <ObservabilityKpiSection items={observabilityItems} /> : null}
    </div>
  );
}

export function AlertBannerSection({ interactions = {}, model }) {
  const messages = Array.isArray(model.messages) ? model.messages : [];
  if (!messages.length) {
    return null;
  }

  return (
    <section
      className="grid gap-3 rounded-[22px] border border-border-danger-muted bg-[linear-gradient(180deg,rgba(255,244,243,0.96),rgba(255,255,255,0.98))] px-5 py-4 shadow-card"
      data-dispatch-alert-capture-context={serializeDispatchAlertCaptureContext(
        buildNonComplianceAlertCaptureContext(messages),
      ) || undefined}
      data-dispatch-alert-capture-type="non_compliance"
      role="alert"
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="grid gap-1">
          <p className={sectionTagClassName}>Configuration camion invalide</p>
          <h2 className="font-display text-[clamp(1.12rem,1.55vw,1.48rem)] font-semibold leading-[1.06] tracking-[-0.03em] text-danger">Certains camions sont non conformes</h2>
        </div>
        <div>
          <ActionButton
            tone="ghost"
            size="sm"
            onClick={() => {
              interactions.onOpenError?.({
                filter: "invalid_truck_configuration",
                source: "fleet_configuration",
                viewName: "errors",
              });
            }}
          >
            Voir dans Alertes
          </ActionButton>
        </div>
      </div>

      {messages.map((message, index) => (
        <p key={`alert-message-${index}`} className="m-0 text-[0.92rem] leading-7 text-muted-strong">{message}</p>
      ))}
    </section>
  );
}

function IncludeSuspendedDeliveriesControl({ checked = false, disabled = false, onChange = null }) {
  const controlId = React.useId();

  return (
    <label
      htmlFor={controlId}
      className={joinClasses([
        "inline-flex min-h-7 items-center gap-1.5 rounded-sm border border-[rgba(217,119,6,0.24)] bg-[rgba(255,251,245,0.96)] px-2 py-1 text-[0.62rem] font-semibold text-[rgb(146,64,14)]",
        disabled ? "cursor-not-allowed opacity-55" : "cursor-pointer hover:border-[rgba(217,119,6,0.36)] hover:bg-[rgba(255,247,237,0.98)]",
      ])}
      title="Inclure les commandes suspendues au prochain clic sur Optimiser"
    >
      <input
        id={controlId}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => {
          onChange?.(event.target.checked);
        }}
        className="h-3.5 w-3.5 accent-[rgb(217,119,6)]"
        data-include-suspended-deliveries-toggle="true"
      />
      <span className="whitespace-nowrap">Inclure les suspendues</span>
    </label>
  );
}

export function SuspendedPanelSection({ interactions = {}, model }) {
  const items = Array.isArray(model.items) ? model.items : [];
  const includeSuspendedDeliveriesInOptimization = model.includeSuspendedDeliveriesInOptimization === true;
  const handleIncludeSuspendedDeliveriesChange = typeof interactions.onIncludeSuspendedDeliveriesInOptimizationChange === "function"
    ? interactions.onIncludeSuspendedDeliveriesInOptimizationChange
    : null;

  return (
    <PanelShell
      eyebrow="Commandes suspendues"
      title="Consulter les commandes retirées des tournées actives"
      description="Cette vue reste séparée des alertes techniques. Une suspension est un choix manuel persistant jusqu&apos;à une remise en optimisation explicite."
      actions={(
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          {model.hasItems ? (
            <IncludeSuspendedDeliveriesControl
              checked={includeSuspendedDeliveriesInOptimization}
              onChange={handleIncludeSuspendedDeliveriesChange}
              disabled={!handleIncludeSuspendedDeliveriesChange}
            />
          ) : null}
          <Pill tone="warning" data-suspended-panel-count="true">{model.countLabel}</Pill>
        </div>
      )}
      bodyClassName="grid gap-4"
    >
      <div className="grid gap-2.5" data-suspended-panel-list="true">
        <SuspendedDeliveriesList
          items={items}
          onReactivateDelivery={interactions.onReactivateDelivery}
          reassignmentRebuildMode={interactions.reassignmentRebuildMode}
        />
      </div>

      <EmptyState
        tone="subtle"
        title="Aucune commande suspendue"
        message="Les suspensions manuelles apparaîtront ici avec un bouton de réactivation explicite."
        className={model.hasItems ? "hidden" : ""}
        data-suspended-panel-empty="true"
        hidden={model.hasItems}
        aria-hidden={model.hasItems ? "true" : "false"}
      />
    </PanelShell>
  );
}

export function RouteSequenceSection({ interactions = {}, model, hovercardContext = null }) {
  const highlightedRouteSegmentIndex = Number.isInteger(interactions?.highlightedRouteSegmentIndex)
    ? interactions.highlightedRouteSegmentIndex
    : null;

  return (
    <RouteSequencePanel
      highlightedRouteSegmentIndex={highlightedRouteSegmentIndex}
      hovercardContext={hovercardContext}
      interactions={interactions}
      model={model}
    />
  );
}

export function InvalidStopsSection({ interactions = {}, model }) {
  return <InvalidStopsPanel interactions={interactions} model={model} />;
}

export function SupportPanelsSection({ interactions = {}, model }) {
  const routeSequenceModel = model.routeSequence && typeof model.routeSequence === "object"
    ? model.routeSequence
    : {};
  const invalidStopsModel = model.invalidStops && typeof model.invalidStops === "object"
    ? model.invalidStops
    : {};
  const hovercardContext = model.fleet_segment_hovercard_context && typeof model.fleet_segment_hovercard_context === "object"
    ? model.fleet_segment_hovercard_context
    : null;

  return (
    <div className="grid gap-2">
      <RouteSequenceSection interactions={interactions} model={routeSequenceModel} hovercardContext={hovercardContext} />

      {invalidStopsModel.hasItems ? (
        <InvalidStopsSection interactions={interactions} model={invalidStopsModel} />
      ) : null}
    </div>
  );
}
